/**
 * Firestore och externa anrop för "Nytt från bokningsmail".
 *
 * skapaFranBokningsmail() skriver kund, kontakt, lead och möte i EN batch:
 * går något fel skapas ingenting.
 */
import {
  collection, doc, getDocs, query, orderBy, writeBatch, increment, arrayUnion, updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { authFetch } from '../../utils/authFetch';
import { nyttLeadSkedesfalt, skedesbyteFalt } from './pipelineData';
import { LEAD_KALLA_COMDEVA } from './bokningsmail';

const COMDEVA_RELATION = 'Uppringd av Comdeva';

export async function laddaKontakter(companyId) {
  if (!companyId) return [];
  const snap = await getDocs(query(collection(db, 'crmCompanies', companyId, 'contacts'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** BolagsAPI via Netlify-funktionen. Kastar Error med användbar text. */
export async function hamtaBolag(orgNr) {
  const res = await authFetch(`/.netlify/functions/bolagsverket?orgnr=${encodeURIComponent(orgNr)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Fel ${res.status}`);
  return data;
}

/** Namnsökning i BolagsAPI. Kastar Error om sökningen inte är tillgänglig. */
export async function sokForetag(namn) {
  const res = await authFetch(`/.netlify/functions/bolagsverket?q=${encodeURIComponent(namn)}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Fel ${res.status}`);
  return data.results || [];
}

const utan = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * plan = {
 *   kund:    { befintligId | null, falt: {…crmCompanies-fält} },
 *   kontakt: { befintligId | null, falt: {…contact-fält} },
 *   lead:    { befintligt: lead | null, falt: {…crmLeads-fält för nytt lead} },
 *   mote:    { start, slut, plats, amne, kallelsetext },
 *   raw, uid,
 * }
 * Returnerar { companyId, contactId, leadId, activityId }.
 */
export async function skapaFranBokningsmail(plan) {
  const { kund, kontakt, lead, mote, raw, uid } = plan;
  const batch = writeBatch(db);
  const nu = new Date();

  // Kund
  const companyRef = kund.befintligId
    ? doc(db, 'crmCompanies', kund.befintligId)
    : doc(collection(db, 'crmCompanies'));
  if (!kund.befintligId) {
    batch.set(companyRef, utan({ ...kund.falt, status: kund.falt.status || 'prospect', createdAt: nu, createdBy: uid }));
  }

  // Kontakt (inom kunden)
  const contactRef = kontakt.befintligId
    ? doc(db, 'crmCompanies', companyRef.id, 'contacts', kontakt.befintligId)
    : doc(collection(db, 'crmCompanies', companyRef.id, 'contacts'));
  if (!kontakt.befintligId) {
    batch.set(contactRef, utan({
      ...kontakt.falt, primary: false, dealRelation: COMDEVA_RELATION, createdAt: nu,
    }));
  }

  const datum = (mote.start || '').slice(0, 10);
  const tid = (mote.start || '').slice(11, 16);
  const slutTid = (mote.slut || '').slice(11, 16);
  const nastaSteg = mote.amne ? `Möte: ${mote.amne}` : 'Möte';
  const summary = [
    `${mote.amne || 'Möte'} — kl. ${tid}${slutTid ? `–${slutTid}` : ''}`,
    mote.plats ? `Plats: ${mote.plats}` : '',
    'Bokat via Comdeva (bokningsmail)',
  ].filter(Boolean).join('\n');
  const motesFalt = {
    demoStatus: 'bokad',
    nextFollowUp: datum,
    nextFollowUpTime: tid,
    nextFollowUpStatus: 'bokad',
    nastaSteg,
    lastActivitySummary: summary,
    lastActivityDate: datum,
    lastActivityType: 'meeting',
    updatedAt: nu,
  };

  // Lead: nytt eller befintligt öppet
  let leadRef;
  if (lead.befintligt) {
    const b = lead.befintligt;
    leadRef = doc(db, 'crmLeads', b.id);
    const skede = ['identified', 'qualified'].includes(b.status) ? skedesbyteFalt(b, 'demo', uid) : {};
    batch.update(leadRef, utan({
      ...motesFalt,
      ...skede,
      activityCount: increment(1),
      contactIds: arrayUnion(contactRef.id),
      ...(b.mainContactId ? {} : { mainContactId: contactRef.id }),
    }));
  } else {
    leadRef = doc(collection(db, 'crmLeads'));
    batch.set(leadRef, utan({
      ...lead.falt,
      ...nyttLeadSkedesfalt('demo', uid),
      ...motesFalt,
      status: 'demo',
      companyId: companyRef.id,
      contactIds: [contactRef.id],
      mainContactId: contactRef.id,
      kalla: LEAD_KALLA_COMDEVA,
      kallaRaw: raw,
      activityCount: 1,
      ownerId: lead.falt.ownerId || uid,
      createdBy: uid,
      createdAt: nu,
    }));
  }

  // Möte som aktivitet på leadet
  const activityRef = doc(collection(db, 'crmLeads', leadRef.id, 'activities'));
  batch.set(activityRef, utan({
    type: 'meeting',
    date: datum,
    direction: 'out',
    summary,
    participants: [{ name: kontakt.falt.name || '', email: kontakt.falt.email || '', contactId: contactRef.id }],
    contactId: contactRef.id,
    contactName: kontakt.falt.name || '',
    start: mote.start,
    slut: mote.slut,
    plats: mote.plats,
    amne: mote.amne,
    kallelsetext: mote.kallelsetext,
    kalla: LEAD_KALLA_COMDEVA,
    ...(lead.befintligt ? { kallaRaw: raw } : {}),
    kallelseStatus: 'ej öppnad',
    createdBy: uid,
    createdAt: serverTimestamp(),
  }));

  await batch.commit();
  return { companyId: companyRef.id, contactId: contactRef.id, leadId: leadRef.id, activityId: activityRef.id };
}

/** Markerar att kallelsen öppnats i Outlook (eller laddats ner som ICS). */
export async function markeraKallelseOppnad(leadId, activityId, via) {
  await updateDoc(doc(db, 'crmLeads', leadId, 'activities', activityId), {
    kallelseStatus: 'öppnad',
    kallelseVia: via,
    kallelseOppnadAt: serverTimestamp(),
  });
}
